import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { imgUrl } from "../../lib/api";

function Cta({ banner }) {
  if (!banner.button_label || !banner.link) return null;
  const cls = "btn-gold mt-8";
  if (banner.link.startsWith("/")) return <Link to={banner.link} className={cls} data-testid="banner-cta">{banner.button_label}</Link>;
  return <a href={banner.link} target="_blank" rel="noopener noreferrer" className={cls} data-testid="banner-cta">{banner.button_label}</a>;
}

export default function BannerCarousel({ banners, interval = 6, fallback }) {
  const slides = banners?.length ? banners : fallback ? [fallback] : [];
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const total = slides.length;

  const go = useCallback((d) => setIdx((i) => (i + d + total) % total), [total]);

  useEffect(() => {
    if (idx >= total) setIdx(0);
  }, [idx, total]);

  useEffect(() => {
    if (total < 2 || paused) return undefined;
    const t = setInterval(() => go(1), Math.max(2, interval) * 1000);
    return () => clearInterval(t);
  }, [total, paused, interval, go]);

  if (!total) return null;
  const b = slides[Math.min(idx, total - 1)];

  return (
    <section className="relative h-[72vh] min-h-[460px] w-full overflow-hidden bg-black md:h-[82vh]" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} data-testid="banner-carousel">
      <AnimatePresence mode="sync">
        <motion.div key={b.id || "fallback"} className="absolute inset-0" initial={{ opacity: 0, scale: 1.06 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}>
          <img src={imgUrl(b.image)} alt={b.title || "EMAIA PERFUMES"} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/45 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0C] via-transparent to-transparent" />
        </motion.div>
      </AnimatePresence>
      <div className="relative z-10 mx-auto flex h-full max-w-7xl items-center px-5 lg:px-8">
        <motion.div key={`t-${b.id || "f"}`} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.25 }} className="max-w-xl">
          <p className="eyebrow mb-5">EMAIA PERFUMES</p>
          {b.title && <h1 className="font-serif text-4xl font-medium leading-[1.05] text-[#FAF6F0] sm:text-5xl lg:text-6xl" data-testid="banner-title">{b.title}</h1>}
          {b.description && <p className="mt-5 max-w-md text-base leading-relaxed text-[#E8E2D6]/80 md:text-lg" data-testid="banner-description">{b.description}</p>}
          <Cta banner={b} />
        </motion.div>
      </div>
      {total > 1 && (
        <>
          <button onClick={() => go(-1)} className="absolute left-4 top-1/2 z-20 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-[#D4AF37]/40 bg-black/40 text-[#F3E5AB] backdrop-blur transition-colors hover:bg-[#D4AF37]/20 md:flex" aria-label="Anterior" data-testid="banner-prev"><ChevronLeft className="h-5 w-5" /></button>
          <button onClick={() => go(1)} className="absolute right-4 top-1/2 z-20 hidden h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-[#D4AF37]/40 bg-black/40 text-[#F3E5AB] backdrop-blur transition-colors hover:bg-[#D4AF37]/20 md:flex" aria-label="Próximo" data-testid="banner-next"><ChevronRight className="h-5 w-5" /></button>
          <div className="absolute bottom-8 left-1/2 z-20 flex -translate-x-1/2 gap-2">
            {slides.map((s, i) => (
              <button key={s.id || i} onClick={() => setIdx(i)} className={`h-1 rounded-full transition-[width,background-color] duration-500 ${i === idx ? "w-10 bg-[#D4AF37]" : "w-5 bg-white/30"}`} aria-label={`Banner ${i + 1}`} data-testid={`banner-dot-${i}`} />
            ))}
          </div>
        </>
      )}
    </section>
  );
}
